import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import {
  formatAccountDisplayName,
  formatCurrency,
  formatDate,
  formatDocumentType,
} from '../../utils/scanReportUtils';

/**
 * AccountStatementSummary renders a table of accounts and their statement periods
 * with beginning and ending balances. Used to display the summary scan results.
 *
 * @param {Object} props
 * @param {Array} props.accounts - Array of account objects from statement_summary.accounts
 */
const AccountStatementSummary = ({ accounts }) => {
  if (!accounts || accounts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No accounts found in this statement.
      </Typography>
    );
  }

  // Flatten accounts × statements into rows
  const rows = accounts.flatMap((account) =>
    (account.statements || []).map((stmt) => ({
      displayName: formatAccountDisplayName(account),
      accountType: account.account_type,
      accountCategory: account.account_category,
      documentType: formatDocumentType(account.document_type),
      beginDate: stmt.begin_date,
      endDate: stmt.end_date,
      beginBalance: stmt.begin_balance,
      endBalance: stmt.end_balance,
      netChange: stmt.computed_transaction_sum,
      beginDateInferred: stmt.begin_date_inferred,
      hasTransactionsOutsidePeriod: stmt.has_transactions_outside_period,
      transactionsOutsidePeriodCount: stmt.transactions_outside_period_count,
    }))
  );

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Account</TableCell>
            <TableCell>Period</TableCell>
            <TableCell align="right">Beginning Balance</TableCell>
            <TableCell align="right">Ending Balance</TableCell>
            <TableCell align="right">Net Change</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => {
            const netChange = row.netChange != null
              ? parseFloat(row.netChange)
              : parseFloat(row.endBalance) - parseFloat(row.beginBalance);

            return (
              <TableRow key={`${row.displayName}-${row.endDate}-${idx}`}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {row.displayName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Chip label={row.accountCategory} size="small" variant="outlined" />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(row.beginDate)}
                    {row.beginDateInferred && (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: 'warning.main', ml: 0.25 }}
                        title="Start date was estimated (not explicitly stated on the statement)"
                      >
                        *
                      </Typography>
                    )}
                    {' — '}{formatDate(row.endDate)}
                  </Typography>
                  {row.hasTransactionsOutsidePeriod && (
                    <Typography variant="caption" color="warning.main">
                      {row.transactionsOutsidePeriodCount} transaction(s) outside statement period
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{formatCurrency(row.beginBalance)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(row.endBalance)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={
                      netChange > 0
                        ? 'success.main'
                        : netChange < 0
                          ? 'error.main'
                          : 'text.primary'
                    }
                    fontWeight={500}
                  >
                    {netChange > 0 ? '+' : ''}
                    {formatCurrency(netChange)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

AccountStatementSummary.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      bank_name: PropTypes.string,
      account_type: PropTypes.string,
      account_category: PropTypes.string,
      account_number_last4: PropTypes.string,
      document_type: PropTypes.string,
      statements: PropTypes.arrayOf(
        PropTypes.shape({
          begin_date: PropTypes.string,
          end_date: PropTypes.string,
          begin_balance: PropTypes.string,
          end_balance: PropTypes.string,
          computed_transaction_sum: PropTypes.number,
        })
      ),
    })
  ),
};

export default AccountStatementSummary;